import { Alert, ExclamationTriangleIcon, Flex } from "@fluentui/react-northstar";
import { FieldDescriptor } from "@shopify/react-form-state";
import { TeamsSurveyDeliveryPreferenceType } from "@teams/types";
import { TeamInfoDTO } from "@teams/types/api";
import React, { useEffect, useState } from "react";

import { useUserInfo } from "../../../_shared/actions/useUserInfo";
import { DropdownField } from "../../../_shared/components/fields";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { SettingLabel } from "./SettingLabel";

export const DeliveryMethodField: React.FC<{
  field: FieldDescriptor<TeamsSurveyDeliveryPreferenceType>;
  label: string;
  teamsDetails: TeamInfoDTO | undefined;
  fluid?: boolean;
  isMobile?: boolean;
  disabled?: boolean;
  compressed?: boolean;
}> = ({ field, label, fluid, teamsDetails, disabled, isMobile, compressed }) => {
  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const [deliveryError, setDeliveryError] = useState<string>();

  useEffect(() => {
    if (!deliveryError) {
      return;
    }

    const timeout = setTimeout(() => {
      setDeliveryError(undefined);
    }, 5000);

    return () => clearTimeout(timeout);
  }, [deliveryError]);

  return (
    <Flex column>
      <Flex vAlign="center" wrap>
        <SettingLabel icon="send-icon" content={label} compressed={compressed} />

        <DropdownField<TeamsSurveyDeliveryPreferenceType>
          field={field}
          className="polly-authoring__dropdown"
          fluid={fluid}
          isMobile={isMobile}
          disabled={disabled}
          items={[
            {
              label:
                teamsContext.channel?.id && teamsContext.meeting?.id
                  ? "in meeting"
                  : teamsContext.channel?.id
                  ? `in ${teamsContext.team?.displayName} > ${teamsContext.channel?.displayName} channel`
                  : "in chat",
              value: "in-group",
            },
            {
              label: teamsDetails?.memberCount
                ? `as direct message to all ${teamsDetails.memberCount} members of ${
                    teamsContext.team?.displayName || "this chat"
                  }`
                : "as direct message to all members",
              value: "direct-message",
            },
          ]}
          onChanged={item => {
            if (item === "direct-message") {
              if (!teamsDetails?.memberCount) {
                setDeliveryError(
                  "Polly must first be installed to this team or chat to use direct message option"
                );
                return true;
              }

              if (teamsDetails.memberCount > user.featureFlags.surveyAsDmMemberLimit) {
                setDeliveryError(
                  `Send as direct message is only available to teams, chats, or meetings with ${user.featureFlags.surveyAsDmMemberLimit} members or less`
                );
                return true;
              }
            }

            return false;
          }}
        />
      </Flex>

      {deliveryError && (
        <Alert icon={<ExclamationTriangleIcon />} warning content={deliveryError} attached="top" />
      )}
    </Flex>
  );
};
